// The "Vendor Cafe" box on a Bozzuto invoice (Stephanie 2026-10-30): the
// property name and details to type into Vendor Cafe, whether its proofs are
// all approved yet, and the "Uploaded to Vendor Cafe" tick. The property name
// can be changed for this one invoice (rarely billed to another building).
// Buildings not in Vendor Cafe say to send the invoice the normal way.
// Shows nothing for other accounts, or until the database step has run.
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { CopyValue, invoicePdfHref, vcAmount, vcDate, vcMoney, vcUploadedLine, type VendorCafeInvoice } from "./shared";

interface Resp {
  available: boolean;
  uses: boolean;
  notInVendorCafe: boolean;
  invoice: VendorCafeInvoice | null;
}

const CARD = "rounded-md border border-rule bg-white p-[22px]";

export function VendorCafeInvoiceCard({ invoiceId }: { invoiceId: string }) {
  const qc = useQueryClient();
  const key = ["invoices", invoiceId, "vendor-cafe"];
  const q = useQuery({ queryKey: key, queryFn: () => api<Resp>(`/invoices/${invoiceId}/vendor-cafe`), retry: false });
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: { property?: string | null; uploaded?: boolean }) =>
      api<Resp>(`/invoices/${invoiceId}/vendor-cafe`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      void qc.invalidateQueries({ queryKey: ["vendor-cafe"] });
      setEditing(null);
      setError(null);
    },
    onError: (e) => setError(apiErrorMessage(e, "We couldn't save that. Nothing was changed — try again.")),
  });
  const d = q.data;
  if (!d?.available || !d.invoice) return null;
  if (!d.uses) {
    if (!d.notInVendorCafe) return null;
    return (
      <section className={CARD}>
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Vendor Cafe</h2>
        <p className="mt-2 text-sm text-ink">This building isn't in Vendor Cafe. Send this invoice the normal way.</p>
      </section>
    );
  }
  const i = d.invoice;
  return (
    <section className={CARD} aria-labelledby="vendor-cafe-title">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="vendor-cafe-title" className="text-lg font-semibold tracking-[-0.4px] text-ink">Vendor Cafe</h2>
        <Link to="/finance/vendor-cafe" className="text-xs font-medium text-muted hover:text-ink">All Vendor Cafe invoices</Link>
      </div>
      <p className="mt-1 text-sm">
        {i.uploadedAt ? (
          <span className="font-medium text-ink">{vcUploadedLine(i)}</span>
        ) : i.proofsApproved ? (
          <span className="font-medium text-ink">Ready to upload — every proof is approved.</span>
        ) : (
          <span className="text-muted">Waiting on proofs — upload once every proof is approved.</span>
        )}
      </p>
      {editing !== null ? (
        <form
          className="mt-3 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate({ property: editing.trim() || null });
          }}
        >
          <label className="block text-xs font-medium text-muted">
            Property name for this invoice
            <input
              value={editing}
              onChange={(e) => setEditing(e.target.value)}
              placeholder={i.buildingProperty}
              autoFocus
              className="mt-1 block h-9 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink"
            />
          </label>
          <p className="text-xs text-muted">Leave blank to use the building's: {i.buildingProperty}</p>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </form>
      ) : (
        <dl className="mt-3 space-y-2 text-sm">
          <Row label="Property name">
            <CopyValue value={i.property} label="property name" />
          </Row>
          {i.propertyOverride ? <p className="text-right text-xs text-muted">Billed to a different building than {i.clientName}</p> : null}
          <Row label="Type">Invoice</Row>
          <Row label="Invoice No.">
            <CopyValue value={String(i.invoiceNumber)} label="invoice number" />
          </Row>
          <Row label="Invoice date">
            <CopyValue value={vcDate(i.invoiceDate)} label="invoice date" />
          </Row>
          <Row label="Invoice total">
            <CopyValue value={vcAmount(i.total)} label="invoice total" shown={vcMoney(i.total)} />
          </Row>
        </dl>
      )}
      {error && <p className="mt-2 text-xs text-rust-600">{error}</p>}
      {editing === null && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <a href={invoicePdfHref(i.publicShareUuid)} target="_blank" rel="noopener noreferrer" className="inline-flex h-8 items-center rounded-md border border-rule bg-white px-3 text-xs font-medium text-ink hover:bg-cream-50">
            Open PDF
          </a>
          {i.uploadedAt ? (
            <Button type="button" size="sm" variant="ghost" disabled={save.isPending} onClick={() => save.mutate({ uploaded: false })}>
              Undo uploaded
            </Button>
          ) : (
            <Button type="button" size="sm" disabled={save.isPending} onClick={() => save.mutate({ uploaded: true })}>
              Mark uploaded
            </Button>
          )}
          <button type="button" onClick={() => { setError(null); setEditing(i.propertyOverride ?? ""); }} className="text-xs font-medium text-muted hover:text-ink">
            Billed to another building?
          </button>
        </div>
      )}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right font-medium text-ink">{children}</dd>
    </div>
  );
}
